<template>
  <main id="main-content" class="flex-display-body container py-5">
    <page-loading-wrapper :is-loading="fetchingGroups">
      <template>
        <div class="row">
          <div class="col-12">
            <h1 class="display-4 pb-3">Create New Group</h1>
            <template v-if="groupPrefixSuggestions.length">
              <b-form @submit.prevent="createGroup">
                <div :class="{ 'invalid': $v.form.groupPrefix.$error }">
                  <app-group-prefix
                    :groupPrefixSuggestions="groupPrefixSuggestions"
                    :stem="stem"
                    @groupPrefixSelected="onGroupPrefixSelected"
                    :v="$v.form.groupPrefix">
                  </app-group-prefix>
                  <p id="err-prefix-required" v-if="!$v.form.groupPrefix.required"
                     class="prefix-error">This is a required field.</p>
                  <p id="err-prefix-match" v-if="!$v.form.groupPrefix.mustMatchPrefix"
                     class="prefix-error">Please use a prefix from the dropdown list.</p>
                  <b-form-checkbox
                    id="joinPrefixGroup"
                    class="mb-2"
                    v-model="form.joinPrefixGroupStatus"
                    value="join"
                    unchecked-value="do_not_join">
                    Make this group a member of the prefix group above
                  </b-form-checkbox>
                  <p>See
                    <b-link to="/help">group namespaces</b-link>
                    for more information about parent-child relationships among groups.</p>
                </div>
                <div class="pt-4">
                  <b-form-group
                    label="Group Identifier"
                    label-for="groupIdentifierField"
                    :class="{ 'invalid': $v.form.groupIdentifier.$error }">
                    <b-form-input
                      id="groupIdentifierField"
                      v-model.trim="$v.form.groupIdentifier.$model"
                      type="text" ></b-form-input>
                    <b-form-text>*Required. Group Identifier should be lowercase</b-form-text>
                    <p id="err-id-required" v-if="!$v.form.groupIdentifier.required"
                       class="error-message">This is a required field.</p>
                    <p id="err-id-maxlength" v-if="!$v.form.groupIdentifier.maxLength"
                       class="error-message">Must be no longer than
                      {{$v.form.groupIdentifier.$params.maxLength.max}} characters.
                    </p>
                  </b-form-group>
                </div>
                <div class="pt-4">
                  <b-form-group
                    label="Contact Person PID"
                    label-for="contactPIDField"
                    :class="{ 'invalid': $v.form.contactPID.$error }">
                    <b-form-input
                      id="contactPIDField"
                      v-model.trim="$v.form.contactPID.$model"
                      type="text"></b-form-input>
                    <b-form-text>*Required. Must be someone other than {{username}}</b-form-text>
                    <p id="err-pid-required" v-if="!$v.form.contactPID.required"
                       class="error-message">This is a required field.</p>
                    <p id="err-pid-username" v-if="!$v.form.contactPID.mustNotBeCurrentUsername"
                       class="error-message">Must not be {{username}}.</p>
                    <b-form-checkbox
                      class="mt-2"
                      id="addAdminAsContact"
                      v-model="form.addAdminAsContactStatus"
                      value="add"
                      unchecked-value="do_not_add">Add {{username}} as contact.
                      In order to receive group e-mail notifications,
                      the admin must be added as a contact as well.
                    </b-form-checkbox>
                  </b-form-group>
                </div>
                <b-button
                  id="btn-create-group"
                  class="mt-4"
                  type="submit"
                  variant="primary"
                  @click.prevent="createGroup"
                  :disabled="$v.form.$invalid || form.submitting === true">
                  {{form.submitting === true ? 'Submitting...' : 'Create'}}
                </b-button>
              </b-form>
            </template>
            <template v-else>
              <app-no-administered-groups-alert></app-no-administered-groups-alert>
            </template>
          </div>
        </div>
      </template>
    </page-loading-wrapper>
  </main>
</template>

<script>
import { required, maxLength } from 'vuelidate/lib/validators';
import { auth, groupsFetch } from '../util/groups-mixin';
import GroupPrefix from './GroupPrefix';
import NoAdministeredGroupsAlert from './NoAdministeredGroupsAlert';
import PageLoadingWrapper from './slots/PageLoadingWrapper';

export default {
  title: 'Create Group',
  mixins: [auth, groupsFetch],
  data() {
    return {
      form: {
        groupPrefix: '',
        joinPrefixGroupStatus: 'do_not_join',
        addAdminAsContactStatus: 'add',
        groupIdentifier: '',
        contactPID: '',
        submitting: false,
      },
      // Override default values for userRoles and withRoles from groupsFetch mixin
      userRoles: ['administrator'],
      withRoles: ['administrators'],
    };
  },
  props: {
    stem: {
      type: String,
      default: '',
    },
  },
  validations: {
    form: {
      groupPrefix: {
        required,
        mustMatchPrefix() {
          /* Return true if input field contains a match from
          *  the list of group prefix suggestions */
          return this.groupPrefixSuggestions.includes(this.form.groupPrefix);
        },
      },
      groupIdentifier: {
        required,
        maxLength: maxLength(64),
      },
      contactPID: {
        required,
        mustNotBeCurrentUsername(string) {
          /* Return true if contactPID doesn't match username of logged in user */
          return string !== this.username;
        },
      },
    },
  },
  methods: {
    onGroupPrefixSelected(prefix) {
      this.form.groupPrefix = prefix;
    },
    createGroup() {
      this.$v.$touch();
      if (!this.$v.$invalid) {
        this.form.submitting = true;
        this.$store.dispatch('createNewGroup', {
          joinPrefixGroupStatus: this.form.joinPrefixGroupStatus,
          uugid: this.groupIdToCreate,
          contactPid: this.form.contactPID.toLowerCase(),
          addAdminAsContactStatus: this.form.addAdminAsContactStatus,
          groupPrefix: this.form.groupPrefix.toLowerCase(),
        }).finally(() => {
          this.form.submitting = false;
        });
      }
    },
  },
  computed: {
    groupIdToCreate() {
      const lowercaseGroupIdentifier = this.form.groupIdentifier.toLowerCase();
      return `${this.form.groupPrefix}.${lowercaseGroupIdentifier}`;
    },
    groupPrefixSuggestions() {
      return this.$store.getters.administeredGroups.map(administeredGroup => administeredGroup.key);
    },
  },
  components: {
    appGroupPrefix: GroupPrefix,
    appNoAdministeredGroupsAlert: NoAdministeredGroupsAlert,
    PageLoadingWrapper,
  },
};
</script>



// WEBPACK FOOTER //
// src/components/CreateGroup.vue